import { Maximize2Icon, Minimize2Icon } from "lucide-react";
import { memo } from "react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

/**
 * Give the right panel the whole window, or hand the transcript its half back.
 *
 * Ported from t3code's `RightPanelMaximizeControl`. It only appears while the
 * panel is open, because "maximize" has no meaning against a closed panel — a
 * permanently visible control that does nothing most of the time trains people
 * to ignore that corner of the bar.
 *
 * Sits beside the two panel toggles rather than inside the panel: all three
 * answer "how is this window divided", and t3code groups them the same way.
 */
export const RightPanelMaximizeControl = memo(function RightPanelMaximizeControl({
  maximized,
  onToggle,
}: {
  readonly maximized: boolean;
  readonly onToggle: () => void;
}) {
  const label = maximized ? "Restore panel size" : "Maximize panel";
  const Icon = maximized ? Minimize2Icon : Maximize2Icon;

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-pressed={maximized}
            aria-label={label}
            onClick={onToggle}
            data-testid="chatheader-maximize-toggle"
            className={cn(
              // Same geometry as its neighbours: the row reads as one control
              // group, and Forge's 48px touch minimum applies until `sm`.
              "inline-flex size-11 shrink-0 items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:size-8",
              maximized && "bg-muted/80 text-foreground",
            )}
          >
            <Icon aria-hidden="true" className="size-4" />
          </button>
        }
      />
      <TooltipPopup side="bottom">{label}</TooltipPopup>
    </Tooltip>
  );
});
